import { useSyncExternalStore } from 'react';
import type { User } from '@erp/shared';
import { getToken, setToken } from '@/lib/api-client';
import { isKeycloakMode } from '@/config/env';
import { getKeycloak } from '@/auth/keycloak';
import {
  getImpersonationToken,
  IMPERSONATION_USER_KEY,
  isImpersonationPage,
} from '@/auth/impersonation';

const SESSION_USER_KEY = 'erp.user';
const listeners = new Set<() => void>();

/**
 * Onde mora o usuário da sessão. Na aba de depuração (spec 020) é o
 * sessionStorage DELA: o localStorage é da origem, e lá está o admin da outra
 * aba — escrever nele trocaria a pessoa logada lá.
 */
function userStorage(): { storage: Storage; key: string } {
  return isImpersonationPage()
    ? { storage: sessionStorage, key: IMPERSONATION_USER_KEY }
    : { storage: localStorage, key: SESSION_USER_KEY };
}

function readUserRaw(): string | null {
  const { storage, key } = userStorage();
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeUserRaw(value: string | null): void {
  const { storage, key } = userStorage();
  try {
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, value);
  } catch {
    /* sem storage: o usuário vale até recarregar */
  }
}

function emit() {
  for (const l of listeners) l();
}

export function getStoredUser(): User | null {
  const raw = readUserRaw();
  if (!raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

export function isAuthenticated(): boolean {
  // Aba de depuração: autenticada enquanto houver token de sessão — nem Keycloak
  // nem o token do admin entram nesta conta.
  if (isImpersonationPage()) return getImpersonationToken() !== null;
  // Modo keycloak: a fonte de verdade é o keycloak-js (o AuthGate inicializa
  // antes do router renderizar).
  if (isKeycloakMode) return getKeycloak().authenticated ?? false;
  return getToken() !== null;
}

export function loginSession(token: string, user: User) {
  setToken(token);
  setSessionUser(user);
}

/** Salva o usuário da sessão sem tocar no token (modo keycloak: token vive no keycloak-js). */
export function setSessionUser(user: User) {
  writeUserRaw(JSON.stringify(user));
  emit();
}

export function logoutSession() {
  // setToken ignora a aba de depuração; aqui só sai o usuário DESTA aba
  setToken(null);
  writeUserRaw(null);
  emit();
}

/** Mescla campos no usuário salvo (ex.: nome/e-mail editados na Minha conta). */
export function updateStoredUser(patch: Partial<User>) {
  const current = getStoredUser();
  if (!current) return;
  writeUserRaw(JSON.stringify({ ...current, ...patch }));
  emit();
}

let cachedRaw: string | null = null;
let cachedUser: User | null = null;

export function useAuthUser(): User | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => {
      const raw = readUserRaw();
      if (raw !== cachedRaw) {
        cachedRaw = raw;
        cachedUser = raw ? (JSON.parse(raw) as User) : null;
      }
      return cachedUser;
    }
  );
}
